import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FiCreditCard, FiHome, FiCheckCircle, FiShield } from "react-icons/fi";
import { submitPayoutDetails } from "../../api"; // Adjust to your api path

const Enteraccountdetails = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Retrieve item, price, and contractId passed via state
  const contractId = location.state?.contractId;
  const itemTitle = location.state?.itemTitle || "Item / Service";
  const amount = location.state?.price || "450000";

  // Form states
  const [accountNumber, setAccountNumber] = useState("");
  const [accountName, setAccountName] = useState("");
  const [bankName, setBankName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const payload = {
        bankName,
        accountNumber,
        accountName,
        amount: Number(amount)
      };

      // Calls the API linked to the contract ID
      const response = await submitPayoutDetails(contractId, payload);

      if (response.data.success) {
        setShowSuccessModal(true);
      } else {
        setErrorMessage(
  response.data.message || "Failed to submit details."
);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Server error occurred.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoHome = () => {
    navigate("/connect");
  };

  return (
    <div className="bg-gray-50 min-h-screen pb-12 relative">
      {/* 🔝 Sticky Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex items-center justify-center">
        <h1 className="text-lg font-black text-gray-900 tracking-tight">Payout Details</h1>
      </div>

      <div className="max-w-xl mx-auto px-6 pt-8">
        
        {/* 🛡️ Info Banner */}
        <div className="bg-teal-900 rounded-[2.5rem] p-6 mb-8 text-white shadow-xl shadow-teal-100 overflow-hidden relative">
          <div className="absolute right-[-10%] top-[-20%] w-32 h-32 bg-white/10 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
               <div className="p-2 bg-teal-800 rounded-xl"><FiShield className="text-teal-100" size={20} /></div>
               <h2 className="text-sm font-black uppercase tracking-widest">Enter Payout Account</h2>
            </div>
            <p className="text-xs font-bold text-teal-50 leading-relaxed">
              Provide your correct bank account details below to receive your funds for <span className="text-white">{itemTitle}</span> ({Number(amount).toLocaleString()} ₦).
            </p>
          </div>
        </div>

        {errorMessage && (
          <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-2xl text-xs font-bold">
            {errorMessage}
          </div>
        )}

        {/* 📝 Account Details Form */}
        <div className="bg-white p-8 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6">
          <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
            <FiCreditCard className="text-orange-600" /> Bank Information
          </h3>

          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Bank Name */}
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Bank Name</label>
              <input
                type="text"
                required
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                placeholder="e.g., Guaranty Trust Bank (GTB), Access Bank"
                className="w-full bg-gray-50 border-2 border-gray-100 focus:border-orange-500 focus:bg-white rounded-2xl py-4 px-5 font-bold text-xs text-gray-900 transition-all outline-none"
              />
            </div>

            {/* Account Number */}
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Account Number</label>
              <input
                type="text"
                required
                maxLength="10"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ""))}
                placeholder="0123456789"
                className="w-full bg-gray-50 border-2 border-gray-100 focus:border-orange-500 focus:bg-white rounded-2xl py-4 px-5 font-bold text-sm text-gray-900 tracking-widest transition-all outline-none"
              />
            </div>

            {/* Account Name */}
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Account Holder Name</label>
              <input
                type="text"
                required
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                placeholder="Full name as it appears on your bank account"
                className="w-full bg-gray-50 border-2 border-gray-100 focus:border-orange-500 focus:bg-white rounded-2xl py-4 px-5 font-bold text-xs text-gray-900 transition-all outline-none"
              />
            </div>

            {/* Submit Button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-5 bg-green-600 hover:bg-green-700 text-white rounded-[1.5rem] font-black uppercase tracking-[0.2em] text-xs transition-all shadow-xl shadow-green-200 active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSubmitting ? "Processing..." : "Submit Account Details"}
              </button>
            </div>

          </form>
        </div>

        <p className="text-center mt-10 text-[10px] font-black text-gray-300 uppercase tracking-widest">
          Secure Payouts • Instant Settlement • OurASAP
        </p>
      </div>

      {/* 🎉 Success Modal Popup */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] p-8 max-w-sm w-full shadow-2xl border border-gray-100 text-center space-y-6 animate-in fade-in zoom-in duration-200">
            <div className="w-20 h-20 bg-green-100 text-green-600 rounded-[2.5rem] mx-auto flex items-center justify-center shadow-lg shadow-green-100 animate-bounce">
              <FiCheckCircle size={44} />
            </div>
            
            <div className="space-y-2">
              <h3 className="text-xl font-black text-gray-900">Details Received!</h3>
              <p className="text-xs font-bold text-gray-500 leading-relaxed px-2">
                It would be sent to your account. Thank you for using OurASAP!
              </p>
            </div>

            <div className="pt-2">
              <button
                type="button"
                onClick={handleGoHome}
                className="w-full py-5 bg-gray-900 hover:bg-black text-white rounded-[1.5rem] font-black uppercase tracking-[0.2em] text-xs transition-all shadow-xl shadow-gray-300 active:scale-95 flex items-center justify-center gap-3"
              >
                <FiHome size={16} />
                Homepage
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Enteraccountdetails;